import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import { Video, Calendar, Clock, Check, Heart, Shield, Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { format, addDays } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicBookingPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [clinicSettings, setClinicSettings] = useState({ clinic_name: 'Clinic Care OS' });
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  const [form, setForm] = useState({
    patient_name: '',
    mobile: '',
    email: '',
    symptoms: '',
    symptom_duration: '',
    current_medications: '',
    preferred_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    preferred_time: '',
    consent: false
  });

  useEffect(() => {
    fetchClinicSettings();
  }, []);

  const fetchClinicSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        setClinicSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch clinic settings');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    const validFiles = files.filter(file => {
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Use JPG, PNG, or PDF`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large. Maximum 5MB`);
        return false;
      }
      return true;
    });

    if (attachments.length + validFiles.length > 5) {
      toast.error('Maximum 5 attachments allowed');
      return;
    }

    // Create preview for files
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachments(prev => [...prev, {
          file,
          preview: reader.result,
          name: file.name,
          type: file.type
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const uploadAttachmentsToAppointment = async (appointmentId) => {
    for (const attachment of attachments) {
      try {
        const formData = new FormData();
        formData.append('file', attachment.file);
        await axios.post(`${API}/teleconsult/${appointmentId}/attachments`, formData);
      } catch (error) {
        console.error(`Failed to upload ${attachment.name}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.patient_name || !form.mobile || !form.symptoms || !form.preferred_date || !form.preferred_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!form.consent) {
      toast.error('Please provide consent for online consultation');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('patient_name', form.patient_name);
      formData.append('mobile', form.mobile);
      formData.append('email', form.email || '');
      formData.append('symptoms', form.symptoms);
      formData.append('symptom_duration', form.symptom_duration || '');
      formData.append('current_medications', form.current_medications || '');
      formData.append('preferred_date', form.preferred_date);
      formData.append('preferred_time', form.preferred_time);
      formData.append('consent', form.consent);

      const response = await axios.post(`${API}/teleconsult/public-booking`, formData);
      
      // Upload attachments if any
      if (attachments.length > 0) {
        setUploadingFiles(true);
        await uploadAttachmentsToAppointment(response.data.appointment_id);
        setUploadingFiles(false);
      }
      
      setBookingResult(response.data);
      setSubmitted(true);
      toast.success('Booking submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setLoading(false);
    }
  };

  // Generate time slots from 8 AM to 5 PM
  const timeSlots = [];
  for (let hour = 8; hour <= 17; hour++) {
    for (let minute of [0, 30]) {
      if (hour === 17 && minute === 30) break;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(time);
    }
  }

  // Generate available dates (next 14 days, excluding Sundays)
  const availableDates = [];
  for (let i = 1; i <= 14; i++) {
    const date = addDays(new Date(), i);
    if (date.getDay() !== 0) { // Exclude Sundays
      availableDates.push(format(date, 'yyyy-MM-dd'));
    }
  }

  if (submitted && bookingResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">Booking Confirmed!</h2>
            <p className="text-muted-foreground mb-6">
              Your online consultation has been scheduled.
            </p>
            
            <div className="bg-muted/50 rounded-lg p-4 text-left space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="font-medium">{format(new Date(bookingResult.date), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <span className="font-medium">{bookingResult.time}</span>
              </div>
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-primary" />
                <span className="text-sm">Online Consultation</span>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">What's next?</p>
              <p>Our clinic will contact you to confirm your appointment and provide the video meeting link.</p>
            </div>

            <Button 
              className="mt-6 w-full" 
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setForm({
                  patient_name: '',
                  mobile: '',
                  email: '',
                  symptoms: '',
                  symptom_duration: '',
                  current_medications: '',
                  preferred_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
                  preferred_time: '',
                  consent: false
                });
              }}
            >
              Book Another Consultation
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-8 px-4">
      <Toaster position="top-right" richColors />
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {clinicSettings.clinic_logo && (
            <img 
              src={clinicSettings.clinic_logo} 
              alt="Clinic Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl font-bold text-gray-800">
            {clinicSettings.clinic_name || 'Online Consultation'}
          </h1>
          <p className="text-muted-foreground mt-2">
            Book your online video consultation with our doctor
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="text-center p-4 bg-white rounded-xl shadow-sm">
            <Video className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-sm font-medium">Video Consult</p>
            <p className="text-xs text-muted-foreground">From anywhere</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm">
            <Heart className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-medium">Quality Care</p>
            <p className="text-xs text-muted-foreground">Same as in-person</p>
          </div>
          <div className="text-center p-4 bg-white rounded-xl shadow-sm">
            <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-sm font-medium">Secure</p>
            <p className="text-xs text-muted-foreground">Private & safe</p>
          </div>
        </div>

        {/* Booking Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-blue-600" />
              Book Online Consultation
            </CardTitle>
            <CardDescription>
              Fill out the form below to schedule your video consultation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                  Personal Information
                </h3>
                
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={form.patient_name}
                    onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                    placeholder="Juan Dela Cruz"
                    required
                    data-testid="booking-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Mobile Number *</Label>
                    <Input
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                      placeholder="09XX XXX XXXX"
                      required
                      data-testid="booking-mobile"
                    />
                  </div>
                  <div>
                    <Label>Email (optional)</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="your@email.com"
                      data-testid="booking-email"
                    />
                  </div>
                </div>
              </div>

              {/* Medical Information */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                  Medical Information
                </h3>
                
                <div>
                  <Label>What are your symptoms? *</Label>
                  <Textarea
                    value={form.symptoms}
                    onChange={(e) => setForm({ ...form, symptoms: e.target.value })}
                    placeholder="Describe your symptoms or reason for consultation..."
                    rows={3}
                    required
                    data-testid="booking-symptoms"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>How long have you had these symptoms?</Label>
                    <Input
                      value={form.symptom_duration}
                      onChange={(e) => setForm({ ...form, symptom_duration: e.target.value })}
                      placeholder="e.g., 3 days"
                    />
                  </div>
                  <div>
                    <Label>Current Medications</Label>
                    <Input
                      value={form.current_medications}
                      onChange={(e) => setForm({ ...form, current_medications: e.target.value })}
                      placeholder="List any medications"
                    />
                  </div>
                </div>

                {/* File Attachments */}
                <div>
                  <Label>Attachments (optional)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Upload lab results, medical photos, or relevant documents. Max 5 files, 5MB each.
                  </p>
                  
                  <div className="space-y-3">
                    {/* Upload Button */}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        multiple
                        data-testid="booking-attachment-input"
                      />
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 hover:border-primary/50 hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-center gap-2 text-muted-foreground">
                          <Upload className="w-5 h-5" />
                          <span className="text-sm">Click to upload files (JPG, PNG, PDF)</span>
                        </div>
                      </div>
                    </label>

                    {/* Attachment List */}
                    {attachments.length > 0 && (
                      <div className="space-y-2">
                        {attachments.map((att, index) => (
                          <div 
                            key={index}
                            className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg"
                          >
                            {att.type.startsWith('image/') ? (
                              <img 
                                src={att.preview} 
                                alt={att.name}
                                className="w-10 h-10 rounded object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-red-100 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-red-600" />
                              </div>
                            )}
                            <span className="flex-1 text-sm truncate">{att.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAttachment(index)}
                              className="h-8 w-8 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                  Preferred Schedule
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date *</Label>
                    <Select 
                      value={form.preferred_date} 
                      onValueChange={(v) => setForm({ ...form, preferred_date: v })}
                    >
                      <SelectTrigger data-testid="booking-date">
                        <SelectValue placeholder="Select date" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDates.map(date => (
                          <SelectItem key={date} value={date}>
                            {format(new Date(date), 'EEE, MMM d')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Time *</Label>
                    <Select 
                      value={form.preferred_time} 
                      onValueChange={(v) => setForm({ ...form, preferred_time: v })}
                    >
                      <SelectTrigger data-testid="booking-time">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map(time => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Consent */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={form.consent}
                    onCheckedChange={(checked) => setForm({ ...form, consent: checked })}
                    data-testid="booking-consent"
                  />
                  <label htmlFor="consent" className="text-sm cursor-pointer">
                    <span className="font-medium">I consent to receive medical consultation through online communication.</span>
                    <p className="text-muted-foreground mt-1">
                      I understand that this is a video consultation and I agree to provide accurate medical information for proper diagnosis.
                    </p>
                  </label>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={loading || uploadingFiles}
                data-testid="booking-submit"
              >
                {loading ? (uploadingFiles ? 'Uploading files...' : 'Submitting...') : 'Book Consultation'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                After booking, our clinic will contact you to confirm your appointment and provide payment details.
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>Need help? Contact us at {clinicSettings.clinic_phone || 'the clinic'}</p>
        </div>
      </div>
    </div>
  );
};

export default PublicBookingPage;
